import type { ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { CopyButton } from "@/components/CopyButton";
import { DataPanel, MonoLabel, btnOutline, btnPrimary } from "@/components/primitives";
import { LogoFull } from "@/components/Logo";
import { SITE, isCaLive } from "@/lib/config";
import { useWalletConnect, truncateAddr } from "@/lib/wallet";
import { useSEO } from "@/lib/useSEO";
import { PageShell } from "@/components/PageShell";

export default function Token() {
  const { address, connect } = useWalletConnect();

  useSEO({
    title: "$CONSEQ — Token | Consequence Protocol",
    description:
      "$CONSEQ is live on ARC. Official contract address, buy on lift.fun, chart on DexScreener. Tokenomics will be published at launch.",
    path: "/token",
  });

  return (
    <PageShell testId="page-token">
      <section className="container-t pb-16 pt-20 sm:pt-28">
        <div className="grid items-end gap-10 lg:grid-cols-[1fr_auto]">
          <SectionHeader
            index="00"
            label="Token"
            title={
              <>
                <span className="text-cyan glow-cyan">{SITE.ticker}</span> — the consequence asset
              </>
            }
            description="The token page publishes only what is official. The contract is live on ARC; tokenomics will follow at launch."
          />
          <Reveal className="hidden lg:block">
            <LogoFull className="h-36" />
          </Reveal>
        </div>
      </section>

      <section className="container-t pb-24" data-testid="token-panels">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {/* Contract address — spans two columns */}
          <Reveal className="lg:col-span-2">
            <DataPanel title="CONTRACT ADDRESS" meta={`NETWORK · ${SITE.network}`} className="h-full">
              <div data-testid="contract-address-panel" className="flex flex-wrap items-center justify-between gap-4">
                <span className="break-all font-mono text-sm text-cyan" data-testid="ca-full-address">
                  {SITE.contractAddress}
                </span>
                <CopyButton value={SITE.contractAddress} label="Copy CA" testId="token-ca-copy-btn" />
              </div>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-dim/70">
                Official {SITE.tickerPlain} contract on {SITE.network} — verify before you trade
              </p>
            </DataPanel>
          </Reveal>

          <Reveal delay={0.07}>
            <DataPanel title="TOKEN" meta="OFFICIAL" className="h-full">
              <div data-testid="token-panel">
                <span className="font-display text-xl font-bold text-ink">{SITE.ticker}</span>
              </div>
            </DataPanel>
          </Reveal>

          <Reveal delay={0.1}>
            <DataPanel title="NETWORK" meta="OFFICIAL" className="h-full">
              <div data-testid="network-panel">
                <span className="font-mono text-sm text-ink">{SITE.network}</span>
              </div>
            </DataPanel>
          </Reveal>

          <Reveal delay={0.14}>
            <a href={SITE.buyUrl} target="_blank" rel="noopener noreferrer" className="block h-full" data-testid="token-buy-link">
              <DataPanel title="BUY" meta={SITE.buyLabel} className="panel-hover h-full">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-ink">{SITE.buyLabel}</span>
                  <ArrowUpRight className="h-4 w-4 text-cyan" aria-hidden="true" />
                </div>
                <p className="mt-3 text-xs text-dim">Acquire {SITE.ticker} on lift.fun</p>
              </DataPanel>
            </a>
          </Reveal>

          <Reveal delay={0.18}>
            <a href={SITE.chartUrl} target="_blank" rel="noopener noreferrer" className="block h-full" data-testid="token-chart-link">
              <DataPanel title="CHART" meta={SITE.chartLabel} className="panel-hover h-full">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm text-ink">{SITE.chartLabel}</span>
                  <ArrowUpRight className="h-4 w-4 text-cyan" aria-hidden="true" />
                </div>
                <p className="mt-3 text-xs text-dim">Track {SITE.ticker} price on DexScreener</p>
              </DataPanel>
            </a>
          </Reveal>

          <Reveal delay={0.22}>
            <DataPanel title="TOKENOMICS" meta="PENDING" className="h-full">
              <p className="text-sm leading-relaxed text-dim">
                Supply, allocation, distribution, and emissions have not been published. Nothing here constitutes
                economic data. Announcements will be made at launch.
              </p>
            </DataPanel>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-panel/40 py-20" data-testid="token-cta">
        <div className="container-t text-center">
          <Reveal>
            <MonoLabel className="text-[9px]">Live on {SITE.network}</MonoLabel>
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-bold uppercase tracking-tight text-ink sm:text-4xl">
              The CA is <span className="text-cyan glow-cyan">live.</span>
            </h2>
            <div className="mx-auto mt-8 flex max-w-xl flex-wrap items-center justify-center gap-3 border border-line bg-base/70 px-5 py-4" data-testid="token-cta-ca-bar">
              <span className="break-all font-mono text-xs text-cyan sm:text-sm">{SITE.contractAddress}</span>
              <CopyButton value={SITE.contractAddress} label="Copy" testId="token-cta-copy-btn" />
            </div>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <button data-testid="token-connect-btn" className={btnPrimary} onClick={connect} title={address ? address : "Connect your wallet"}>
                {address ? truncateAddr(address) : isCaLive ? "Connect Wallet" : "CA Coming Soon"}
              </button>
              <a href={SITE.chartUrl} target="_blank" rel="noopener noreferrer" data-testid="token-chart-cta" className={btnOutline}>
                Open Chart
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
